import moment from "moment";
import { getSalesByLevel3 } from "@/api/financialReport";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Genera las cabeceras de mes entre initDate y endDate (inclusive),
 * en formato 'MMM YYYY' (ej. "ene 2025"), sin puntos.
 * @param {string} initDate - YYYY-MM-DD
 * @param {string} endDate  - YYYY-MM-DD
 * @returns {string[]}
 */
function buildMonthHeaders(initDate, endDate) {
    const headers = [];
    let cursor = moment(initDate).startOf("month");
    const end = moment(endDate).startOf("month");

    while (cursor.isSameOrBefore(end)) {
        headers.push(cursor.format("MMM YYYY").replace(".", ""));
        cursor.add(1, "months");
    }
    return headers;
}

/**
 * Calcula las fechas de inicio y fin según el rango seleccionado.
 * @param {Date}   endMonth
 * @param {string} selectedPeriodRange
 * @returns {{ initDate: string, endDate: string }}
 */
function resolveDateRange(endMonth, selectedPeriodRange) {
    let refEnd = moment(endMonth).endOf("month");

    if (selectedPeriodRange === "previousYear") {
        refEnd = moment(endMonth).subtract(1, "year").endOf("year");
    }

    const endDate = refEnd.format("YYYY-MM-DD");
    let initDate;

    switch (selectedPeriodRange) {
        case "lastYear":
            initDate = moment(refEnd).subtract(12, "months").startOf("month").format("YYYY-MM-DD");
            break;
        case "last24months":
            initDate = moment(refEnd).subtract(24, "months").startOf("month").format("YYYY-MM-DD");
            break;
        case "actualYear":
            initDate = moment(refEnd).startOf("year").format("YYYY-MM-DD");
            break;
        case "previousYear":
            initDate = moment(refEnd).startOf("year").format("YYYY-MM-DD");
            break;
        default:
            initDate = moment(refEnd).subtract(12, "months").startOf("month").format("YYYY-MM-DD");
    }

    return { initDate, endDate };
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = {
    /** Fecha de referencia (último mes visible) */
    endMonth: moment().toDate(),
    /** Rango de períodos a mostrar */
    selectedPeriodRange: "lastYear",
    /** Filtro de agrupación (Cuentas, Clientes, Tiendas, etc.) */
    activeSubFilter: "cuentas",
    /**
     * Cabeceras de columna calculadas tras el fetch.
     * Ejemplo: ["ene 2025", "feb 2025", ...]
     * @type {string[]}
     */
    monthHeaders: [],
    /**
     * Dict indexado por nombre de cuenta level_3.
     * Cada entrada es: { [periodoLabel]: number }
     * Ejemplo: { "Productos": { "ene 2025": 1500000, ... }, ... }
     * @type {Object.<string, Object.<string, number>>}
     */
    accountsData: {},
    /** Lista ordenada de cuentas level_3 presentes en los datos */
    accounts: [],
    /** Rango real devuelto por el backend */
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
};

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
    /**
     * Construye las filas de la tabla: una por cuenta level_3
     * más una fila de total al final.
     * @returns {Array<{ label: string, values: number[], isTotal: boolean }>}
     */
    tableRows: (state) => {
        return state.accounts.map((account) => ({
            label: account,
            isTotal: false,
            values: state.monthHeaders.map(
                (header) => state.accountsData[account]?.[header] ?? 0
            ),
        }));
    },

    /**
     * Fila de totales: suma de todas las cuentas por período.
     * @returns {{ label: string, values: number[], isTotal: boolean }}
     */
    totalRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            return state.accounts.reduce((sum, account) => {
                return sum + (state.accountsData[account]?.[header] ?? 0);
            }, 0);
        });
        return { label: "Ventas Netas", values, isTotal: true };
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_END_MONTH(state, date) {
        state.endMonth = date;
    },
    SET_SELECTED_PERIOD_RANGE(state, range) {
        state.selectedPeriodRange = range;
    },
    SET_ACTIVE_SUB_FILTER(state, filter) {
        state.activeSubFilter = filter;
    },
    SET_SALES_DATA(state, { accounts, accountsData, monthHeaders, periodRange }) {
        state.accounts = accounts;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    async fetchSalesData({ state, commit }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { initDate, endDate } = resolveDateRange(
                state.endMonth,
                state.selectedPeriodRange
            );

            // Format groupBy parameter (e.g. 'cuentas', 'clientes', or category name)
            const groupBy = state.activeSubFilter.toLowerCase();

            const response = await getSalesByLevel3(initDate, endDate, groupBy);
            const { accounts, report_data: reportData, period_range: periodRange } = response.data;

            // Construir cabeceras de mes dentro del rango real
            const monthHeaders = buildMonthHeaders(periodRange.init_date, periodRange.end_date);

            // Construir dict { account -> { "ene 2025": amount } }
            const accountsData = {};
            accounts.forEach((account) => {
                accountsData[account] = {};
                (reportData[account] ?? []).forEach((item) => {
                    const label = moment(item.period).format("MMM YYYY").replace(".", "");
                    accountsData[account][label] = Number(item.total_amount);
                });
            });

            commit("SET_SALES_DATA", { accounts, accountsData, monthHeaders, periodRange });
        } catch (error) {
            console.error("[salesReport] Error fetching sales data:", error);
            commit("SET_ERROR", error?.response?.data?.detail ?? "Error al cargar ventas.");
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
